import { useMemo, useRef, useState } from 'react';
import { TreeEntry } from '../types';

export interface UseTreeRoverProps {
  onKeyDown: (e: React.KeyboardEvent) => void;
  onFocus: (e: React.FocusEvent) => void;
  tabIndex: number;
  ref: React.RefCallback<HTMLElement>;
}

export interface UseTreeRover {
  getRoverItemProps(item: TreeEntry, path: TreeEntry[]): UseTreeRoverProps;
}

export interface UseTreeRoverOptions {
  items: TreeEntry[];
  expandedIds: Set<string>;
  checkable?: boolean;
  onSelect: (item: TreeEntry, path: TreeEntry[]) => void;
  onExpand: (item: TreeEntry, path: TreeEntry[]) => void;
  onCheck?: (item: TreeEntry, checked: boolean) => void;
  getCheckState?: (item: TreeEntry) => { checked: boolean; indeterminate: boolean };
}

interface FlattenedNode {
  item: TreeEntry;
  path: TreeEntry[];
}

export function useTreeRover(options: UseTreeRoverOptions): UseTreeRover {
  const { items, expandedIds, checkable = false, onSelect, onExpand, onCheck, getCheckState } = options;
  const refs = useRef<Map<string, HTMLElement>>(new Map());
  const [focusId, setFocusId] = useState<string | null>(null);

  const flattened = useMemo(() => {
    const result: FlattenedNode[] = [];
    const traverse = (entries: TreeEntry[], path: TreeEntry[]) => {
      for (const entry of entries) {
        const currentPath = [...path, entry];
        result.push({ item: entry, path: currentPath });
        if (expandedIds.has(entry.id) && entry.children?.length) {
          traverse(entry.children, currentPath);
        }
      }
    };
    traverse(items, []);
    return result;
  }, [items, expandedIds]);
  const focusIndex = flattened.findIndex((n) => n.item.id === focusId);

  const setFocus = (id: string) => {
    setFocusId(id);
    const el = refs.current.get(id);
    if (el) el.focus();
  };

  return {
    getRoverItemProps: (item, path) => {
      const currentIndex = flattened.findIndex((n) => n.item.id === item.id);

      return {
        ref: (elem) => {
          if (elem) {
            refs.current.set(item.id, elem);
          } else {
            refs.current.delete(item.id);
          }
        },
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.defaultPrevented) return;
          let nextIndex = focusIndex;

          switch (e.key) {
            case 'ArrowUp':
              e.preventDefault();
              if (focusIndex > 0) {
                nextIndex = focusIndex - 1;
                setFocus(flattened[nextIndex].item.id);
              }
              break;
            case 'ArrowDown':
              e.preventDefault();
              if (focusIndex < flattened.length - 1) {
                nextIndex = focusIndex + 1;
                setFocus(flattened[nextIndex].item.id);
              }
              break;
            case 'ArrowLeft':
              e.preventDefault();
              if (expandedIds.has(item.id)) {
                onExpand(item, path);
              } else {
                // Move to parent if not root
                if (path.length >= 1) {
                  setFocus(path.at(-1)!.id);
                }
              }
              break;
            case 'ArrowRight':
              e.preventDefault();
              if ((item.children?.length || item.hasChildren) && !expandedIds.has(item.id)) {
                onExpand(item, path);
              } else if (item.children?.length && expandedIds.has(item.id)) {
                // Move to first child
                const firstChildId = item.children[0].id;
                setFocus(firstChildId);
              }
              break;
            case 'Home':
              e.preventDefault();
              if (flattened.length > 0) {
                setFocus(flattened[0].item.id);
              }
              break;
            case 'End':
              e.preventDefault();
              setFocus(flattened[flattened.length - 1].item.id);
              break;
            case 'Enter':
              e.preventDefault();
              if (checkable && onCheck && getCheckState) {
                const { checked } = getCheckState(item);
                onCheck(item, !checked);
              } else {
                onSelect(item, path);
              }
              break;
            case ' ':
              e.preventDefault();
              if (checkable && onCheck && getCheckState) {
                const { checked } = getCheckState(item);
                onCheck(item, !checked);
              } else {
                onSelect(item, path);
              }
              break;
          }
        },
        onFocus: () => {
          if (focusId !== item.id) setFocusId(item.id);
        },
        tabIndex: focusId === item.id || (focusId == null && currentIndex === 0) ? 0 : -1,
      };
    },
  };
}
